import { Stack } from '@fluentui/react';
import React, { useCallback } from 'react';
import { NewMessage } from './NewMessage';
import { useStyles } from './index.styles';
import {
  MessagesListsProps,
  MessagesSections,
  PivotKeys,
} from 'common/components/MessagesSections';
import { NetworkStatus, useQuery } from '@apollo/client';
import {
  SupplierMessages,
  SupplierMessagesVariables,
} from './__generated__/SupplierMessages';

import GET_SUPPLIER_MESSAGE from './SupplierMessages.graphql';
interface SupplierMessageProps
  extends Pick<MessagesListsProps, 'userMessageCounts'> {
  supplierID: string;
  onCreateMessage: () => void;
}

export const SupplierMessage: React.FC<SupplierMessageProps> = ({
  supplierID,
  userMessageCounts,
  onCreateMessage,
}) => {
  const styles = useStyles();
  const [selectedKey, setSelectedKey] = React.useState<string>(
    PivotKeys.received
  );

  const {
    loading: loadingMessages,
    data: messagesData,
    variables: messageListVariables,
    fetchMore,
    refetch,
    networkStatus,
  } = useQuery<SupplierMessages, SupplierMessagesVariables>(
    GET_SUPPLIER_MESSAGE,
    {
      variables: {
        id: supplierID,
        first: 10,
        filter: { _isIncomingMessage: { equalTo: true } },
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );

  const onListChange = useCallback(
    async (listName?: string) => {
      if (listName) setSelectedKey(listName);
      const _isOutgoingMessage = { equalTo: listName === PivotKeys.sent };
      await refetch({
        ...messageListVariables,
        filter: {
          _isOutgoingMessage,
        },
      });
    },
    [refetch, messageListVariables]
  );

  const loadMore = useCallback(async () => {
    await fetchMore({
      variables: {
        ...messageListVariables,
        after:
          messagesData?.supplier?.userMessagesByEntityId.pageInfo.endCursor,
      },
    });
  }, [fetchMore, messageListVariables, messagesData]);

  const data =
    networkStatus === NetworkStatus.setVariables
      ? undefined
      : messagesData?.supplier?.userMessagesByEntityId.nodes;
  return (
    <Stack className={styles.container} tokens={{ padding: '0px 13px' }}>
      <MessagesSections
        userMessageCounts={userMessageCounts}
        entityId={supplierID}
        data={data || []}
        loadingMessages={loadingMessages}
        onListChange={onListChange}
        onLoadMore={loadMore}
        hasNextPage={
          messagesData?.supplier?.userMessagesByEntityId?.pageInfo?.hasNextPage
        }
      >
        <NewMessage
          onCreateMessage={onCreateMessage}
          selectedKey={selectedKey}
          supplierID={supplierID}
        />
      </MessagesSections>
    </Stack>
  );
};
